import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchHomeContent, mapSectionProduct, HomeSection } from '../api/homeContent';
import ProductCard from '../components/ProductCard';
import { useOrg, useTryonEnabled } from '../context/OrgContext';

const categories = [
 {
    id: 1,
    label: 'Eyeglasses',
    img: 'https://tryon.korv.ai/ecommerce-home-page-images/hero.png',
  },
  {
    id: 3,
    label: 'Sunglasses',
    img: 'https://tryon.korv.ai/ecommerce-home-page-images/sunglass.png',
  },
  {
    id: 4,
    label: 'Contact Lenses',
    img: 'https://tryon.korv.ai/ecommerce-home-page-images/contactlens.png',
  },
  {
    id: 5,
    label: 'Blue Light Glasses',
    img: 'https://tryon.korv.ai/ecommerce-home-page-images/bluelensglass.png',
    badge: 'New',
  },
];

const SectionGrid: React.FC<{ section: HomeSection; loading: boolean; onViewAll: () => void }> = ({ section, loading, onViewAll }) => {
  const products = section.products.map(mapSectionProduct);
  return (
    <section className="section">
      <div className="section__header">
        <h2 className="section__title">{section.section_title || ''}</h2>
        <button className="section__link" onClick={onViewAll}>View All →</button>
      </div>
      {loading ? (
        <div className="products-skeleton">
          {Array.from({ length: 4 }).map((_, i) => <div key={i} className="skeleton-card" />)}
        </div>
      ) : products.length > 0 ? (
        <div className="products-grid">
          {products.slice(0, 4).map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      ) : (
        <p style={{ color: 'var(--color-text-muted)', fontSize: 14 }}>No products available.</p>
      )}
    </section>
  );
};

const TRENDING_PLACEHOLDER: HomeSection = {
  id: '',
  section_key: 'trending_now',
  section_title: 'Trending Now',
  banners: [],
  products: [],
};

const ARRIVALS_PLACEHOLDER: HomeSection = {
  id: '',
  section_key: 'top_arrivals',
  section_title: 'New Arrivals',
  banners: [],
  products: [],
};

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const tryonEnabled = useTryonEnabled();
  const org = useOrg();
  const [loading, setLoading] = useState(true);
  const [trendingSection, setTrendingSection] = useState<HomeSection>(TRENDING_PLACEHOLDER);
  const [arrivalsSection, setArrivalsSection] = useState<HomeSection>(ARRIVALS_PLACEHOLDER);
  const [heroBanners, setHeroBanners] = useState<{ url: string | null; title: string | null; subtitle: string | null; description: string | null; button_text: string | null; button_link: string | null }[]>([]);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);

  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEndHandler = () => {
    if (!touchStart || !touchEnd || heroBanners.length <= 1) return;
    const distance = touchStart - touchEnd;
    const minSwipeDistance = 50;
    if (distance > minSwipeDistance) {
      setCurrentBannerIndex((prev) => (prev + 1) % heroBanners.length);
    }
    if (distance < -minSwipeDistance) {
      setCurrentBannerIndex((prev) => (prev - 1 + heroBanners.length) % heroBanners.length);
    }
  };

  useEffect(() => {
    fetchHomeContent().then((homeContent) => {
      if (homeContent.trendingNow) setTrendingSection(homeContent.trendingNow);
      if (homeContent.topArrivals) setArrivalsSection(homeContent.topArrivals);
      if (homeContent.heroBanner?.banners?.length) {
        const activeBanners = homeContent.heroBanner.banners.filter((b) => b.is_active);
        if (activeBanners.length > 0) {
          setHeroBanners(activeBanners.map(b => ({
            url: b.image_url || null,
            title: b.title || null,
            subtitle: b.subtitle || null,
            description: b.description || null,
            button_text: b.button_text || null,
            button_link: b.button_link || null,
          })));
        } else if (homeContent.heroBanner.banners.length > 0) {
          const fallback = homeContent.heroBanner.banners[0];
          setHeroBanners([{
            url: fallback.image_url || null,
            title: fallback.title || null,
            subtitle: fallback.subtitle || null,
            description: fallback.description || null,
            button_text: fallback.button_text || null,
            button_link: fallback.button_link || null,
          }]);
        }
      }
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (heroBanners.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentBannerIndex((prev) => (prev + 1) % heroBanners.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [heroBanners.length]);

  const heroBanner = heroBanners.length > 0 
    ? heroBanners[currentBannerIndex] 
    : { url: null, title: null, subtitle: null, description: null, button_text: null, button_link: null };

  const orgName = org?.org_name || 'Our Store';

  return (
    <div className="home">
      {/* Hero */}
      <section className="hero" onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEndHandler}>
        <div className="hero__content">
          {heroBanners.length > 0 ? (
            (heroBanner.title || heroBanner.subtitle || heroBanner.description || heroBanner.button_text) ? (
              <>
                {heroBanner.subtitle && <p className="hero__eyebrow">{heroBanner.subtitle}</p>}
                {heroBanner.title && <h1 className="hero__headline">{heroBanner.title}</h1>}
                {heroBanner.description && <p className="hero__sub">{heroBanner.description}</p>}
                <div className="hero__btns">
                  {heroBanner.button_text && (
                    <button className="btn btn--primary" onClick={() => navigate(heroBanner.button_link || '/products')}>
                      {heroBanner.button_text}
                    </button>
                  )}
                </div>
              </>
            ) : null
          ) : (
            <>
              <p className="hero__eyebrow">SEE THE WORLD</p>
              <h1 className="hero__headline">
                Clearly.<br />Stylishly.<br />You.
              </h1>
              <p className="hero__sub">
                Discover premium eyewear that matches your style and enhances your vision.
              </p>
              <div className="hero__btns">
                <button className="btn btn--primary" onClick={() => navigate('/products?category=1')}>
                  Shop Eyeglasses
                </button>
                <button className="btn btn--outline" onClick={() => navigate('/products?category=3')}>
                  Shop Sunglasses
                </button>
              </div>
            </>
          )}
        </div>
        <div 
          className="hero__image-wrap" 
          style={{ position: 'relative' }}
        >
          <img
            src={heroBanner.url || 'https://tryon.korv.ai/ecommerce-home-page-images/hero.png'}
            alt="Premium eyewear"
            className="hero__image"
          />
          {heroBanners.length > 1 && (
            <div className="hero__dots" style={{ position: 'absolute', bottom: '20px', left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: '8px' }}>
              {heroBanners.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentBannerIndex(idx)}
                  style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    backgroundColor: idx === currentBannerIndex ? '#fff' : 'rgba(255, 255, 255, 0.5)',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0
                  }}
                  aria-label={`Go to slide ${idx + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Shop by category */}
      <section className="section">
        <h2 className="section__title">Shop By Category</h2>
        <div className="category-grid">
          {categories.map((cat) => (
            <div
              key={cat.id}
              className="category-card"
              onClick={() => navigate(`/products?category=${cat.id}`)}
            >
              {cat.badge && <span className="category-card__badge">{cat.badge}</span>}
              <div className="category-card__img-wrap">
                <img src={cat.img} alt={cat.label} className="category-card__img" />
              </div>
              <p className="category-card__label">{cat.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Trending Now */}
      <SectionGrid section={trendingSection} loading={loading} onViewAll={() => navigate('/products')} />

      {/* Virtual Try-On promo strip — only when the org has the feature enabled. */}
      {tryonEnabled && (
        <section className="vto-promo">
          <div className="vto-promo__card">
            <div className="vto-promo__content">
              <p className="vto-promo__eyebrow">VIRTUAL TRY-ON</p>
              <h2 className="vto-promo__title">Find frames that fit you perfectly</h2>
              <div className="vto-promo__features">
                <span>● Live Try-On</span>
                <span>● 1000+ Frames</span>
                <span>● AI Face Match</span>
              </div>
              <button className="btn btn--primary" onClick={() => navigate('/tryon')}>Try On Now</button>
            </div>
            <div className="vto-promo__image-wrap">
              <img src="/images/tryon-poster.jpg" alt="Virtual try-on" className="vto-promo__image" />
            </div>
          </div>
        </section>
      )}

      {/* New Arrivals */}
      <div className="section--gray">
        <SectionGrid section={arrivalsSection} loading={loading} onViewAll={() => navigate('/products')} />
      </div>

      {/* CTA Banner */}
      <section className="cta-banner">
        <div className="cta-banner__content">
          <h2>Get Your Free Eye Check</h2>
          <p>Visit {orgName} for a complimentary eye examination with every purchase.</p>
          <button className="btn btn--white" onClick={() => navigate('/products')}>
            Shop Now
          </button>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
